import {Component, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {Actions, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {Observable, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {login, loginSuccess} from '../../actions/auth.actions';
import {getErrorMessage} from '../../selectors/auth.selector';
import {AppState} from '../../../reducers';
import {ExternalUserService} from '../../services/external-user/external-user.service';
import {MessageModel} from '../../../shared/models/message.model';
import {CommonRoutes} from '../../../shared/enums/routes.enum';

@Component({
  selector: 'app-external-user-association',
  templateUrl: './external-user-association.component.html',
  styleUrls: ['./external-user-association.component.scss'],
})
export class ExternalUserAssociationComponent implements OnInit, OnDestroy {
  private readonly ASSOCIATION_SUCCESS_MESSAGE = 'Account associato con successo!';
  private readonly ASSOCIATION_ERROR_MESSAGE = 'Errore durante l\'associazione.';
  private readonly PARAMS_ERROR_MESSAGE = 'Parametri mancanti nell\'URL.';

  // Query parameters from URL
  externalService: string | null = null;
  externalUser: string | null = null;
  verificationCode: string | null = null;

  // Form for login
  loginForm = new UntypedFormGroup({
    username: new UntypedFormControl('', [Validators.required]),
    password: new UntypedFormControl('', [Validators.required]),
  });

  // State management
  loginError$: Observable<string | undefined>;
  isLoading = false;
  isAssociating = false;
  associationComplete = false;
  messages: MessageModel[] = [];

  // Routing constants
  CommonRoutes = CommonRoutes;

  private readonly onDestroy = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private store: Store<AppState>,
    private actions$: Actions,
    private externalUserService: ExternalUserService
  ) {
    this.loginError$ = this.store.select(getErrorMessage);
  }

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntil(this.onDestroy)).subscribe(params => {
      this.externalService = params['externalService'];
      this.externalUser = params['externalUser'];
      this.verificationCode = params['verificationCode'];

      // Validate required parameters
      if (!this.hasValidParameters()) {
        this.messages.push(
          new MessageModel(
            this.PARAMS_ERROR_MESSAGE,
            true
          )
        );
      }
    });

    // Listen for successful login to trigger association
    this.actions$
      .pipe(ofType(loginSuccess), takeUntil(this.onDestroy))
      .subscribe(() => {
        if (this.hasValidParameters() && !this.associationComplete) {
          this.associateExternalUser();
        }
      });
  }

  login(): void {
    this.loginForm.markAllAsTouched();
    if (this.loginForm.valid) {
      this.isLoading = true;
      this.messages = [];

      try {
        this.store.dispatch(
          login({
            username: this.loginForm.value.username,
            password: this.loginForm.value.password,
          })
        );
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    }
  }

  private async associateExternalUser(): Promise<void> {
    if (!this.hasValidParameters()) {
      return;
    }

    this.isAssociating = true;
    this.isLoading = true;
    this.messages = [];

    try {
      await this.externalUserService.associateExternalUser(
        this.externalService,
        this.externalUser,
        this.verificationCode
      );
      this.associationComplete = true;
      this.messages.push(
        new MessageModel(
          this.ASSOCIATION_SUCCESS_MESSAGE,
          false
        )
      );
    } catch (error) {
      this.messages.push(new MessageModel(this.ASSOCIATION_ERROR_MESSAGE, true));
    } finally {
      this.isAssociating = false;
      this.isLoading = false;
    }
  }

  public hasValidParameters(): boolean {
    return !!(this.externalService && this.externalUser && this.verificationCode);
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
